import React, { useMemo } from "react";
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

/**
 * ConcurrentSaveDialog (iter-137)
 *
 * Rendered when a PATCH /estimates/{id} comes back with a 409
 * `concurrent_save` — the user's local edits are shown side-by-side
 * with the server's newer version so they can decide whether to
 * overwrite, discard, or keep both in mind and cancel out.
 *
 * Props:
 *   open              boolean
 *   onOpenChange      (bool) → void
 *   detail            409 detail payload
 *                     { last_saved_by_name, last_saved_at, current_saves, ... }
 *   localPatch        the patch object the user just tried to save
 *   serverEstimate    the winning estimate as of the last refresh (or
 *                     null if we haven't fetched it yet — will fall back
 *                     to a "reload to compare" prompt)
 *   onOverwrite       () → void — fire the patch again bypassing the guard
 *   onDiscard         () → void — drop local edits, keep server version
 */
export default function ConcurrentSaveDialog({
    open,
    onOpenChange,
    detail,
    localPatch = {},
    serverEstimate = null,
    onOverwrite,
    onDiscard,
}) {
    const rows = useMemo(
        () => buildDiffRows(localPatch, serverEstimate),
        [localPatch, serverEstimate],
    );

    const savedBy = detail?.last_saved_by_name || "another teammate";
    const savedAt = friendlyTime(detail?.last_saved_at);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl"
                data-testid="concurrent-save-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="w-5 h-5" />
                        Someone else saved this estimate
                    </DialogTitle>
                    <DialogDescription>
                        <span className="font-semibold text-slate-900">{savedBy}</span> saved
                        a newer version{savedAt ? ` ${savedAt}` : ""}. Compare their changes to
                        yours and decide what to keep.
                    </DialogDescription>
                </DialogHeader>

                <div className="my-3 rounded-md border border-slate-200 overflow-hidden">
                    <div className="grid grid-cols-[minmax(120px,1fr)_2fr_2fr] text-[11px] uppercase tracking-wider bg-slate-50 border-b border-slate-200 px-3 py-2 font-bold text-slate-500">
                        <div>Field</div>
                        <div>Your edit</div>
                        <div>Their version</div>
                    </div>
                    {rows.length === 0 ? (
                        <div className="px-3 py-6 text-center text-sm text-slate-500">
                            No field-level diff available — reload the estimate to see their changes.
                        </div>
                    ) : (
                        <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
                            {rows.map((r) => (
                                <div
                                    key={r.field}
                                    className="grid grid-cols-[minmax(120px,1fr)_2fr_2fr] text-sm px-3 py-2 items-start"
                                    data-testid={`diff-row-${r.field}`}
                                >
                                    <div className="font-semibold text-slate-700">{r.label}</div>
                                    <div className="text-emerald-700 whitespace-pre-wrap break-words pr-2">
                                        {r.mine}
                                    </div>
                                    <div className="text-slate-700 whitespace-pre-wrap break-words pl-2 border-l border-slate-200">
                                        {r.theirs}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange?.(false)}
                        data-testid="concurrent-save-cancel"
                    >
                        <X className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onDiscard}
                        data-testid="concurrent-save-discard"
                        className="text-slate-700"
                    >
                        Discard my edits
                    </Button>
                    <Button
                        type="button"
                        onClick={onOverwrite}
                        data-testid="concurrent-save-overwrite"
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                    >
                        Overwrite with mine <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/** Build a compact diff between the user's patch and the server's
 *  current estimate. Only fields that appear in the patch AND differ
 *  from the server value are shown so the dialog stays scannable. */
function buildDiffRows(patch, server) {
    if (!patch) return [];
    const rows = [];
    for (const [field, mine] of Object.entries(patch)) {
        if (field === "expected_saves") continue;
        const theirs = server ? server[field] : undefined;
        const meta = FIELD_META[field] || {
            label: prettyField(field),
            fmt: defaultFmt,
        };
        const mineStr = meta.fmt(mine);
        const theirsStr = server ? meta.fmt(theirs) : "—";
        if (mineStr === theirsStr) continue; // No visible change
        rows.push({
            field,
            label: meta.label,
            mine: mineStr,
            theirs: theirsStr,
        });
    }
    return rows;
}

const currency = (n) =>
    (Number(n) || 0).toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });

const defaultFmt = (v) => {
    if (v === undefined || v === null || v === "") return "—";
    if (typeof v === "number") return String(v);
    if (typeof v === "boolean") return v ? "on" : "off";
    if (Array.isArray(v)) return `${v.length} item${v.length === 1 ? "" : "s"}`;
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
};

const FIELD_META = {
    label: { label: "Title", fmt: (v) => (v || "—") },
    summary: {
        label: "Summary",
        fmt: (v) => (v ? String(v).slice(0, 140) + (String(v).length > 140 ? "…" : "") : "—"),
    },
    report: {
        label: "Report body",
        fmt: (v) => (v ? `${String(v).length} chars` : "—"),
    },
    lines: {
        label: "Line items",
        fmt: (v) => (Array.isArray(v) ? `${v.length} line${v.length === 1 ? "" : "s"}` : "—"),
    },
    profit_margin_pct: {
        label: "Profit margin %",
        fmt: (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`),
    },
    profit_amount: {
        label: "Profit amount",
        fmt: (v) => (v == null ? "—" : currency(v)),
    },
    subtotal_override: {
        label: "Subtotal override",
        fmt: (v) => (v == null ? "cleared" : currency(v)),
    },
    totals: {
        label: "Totals",
        fmt: (v) => (v && typeof v === "object" ? currency(v.grand_total ?? 0) : "—"),
    },
    quote_itemized: {
        label: "Itemized quote",
        fmt: (v) => (v ? "on" : "off"),
    },
    quote_attachments: {
        label: "Quote attachments",
        fmt: (v) => (Array.isArray(v) ? `${v.length} attachment${v.length === 1 ? "" : "s"}` : "—"),
    },
    missing_items: {
        label: "Missing items",
        fmt: (v) => (Array.isArray(v) ? `${v.length} item${v.length === 1 ? "" : "s"}` : "—"),
    },
    questions: {
        label: "Open questions",
        fmt: (v) => (Array.isArray(v) ? `${v.length}` : "—"),
    },
};

function prettyField(field) {
    return String(field || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function friendlyTime(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.round(diffMs / 60000);
        if (diffMin < 1) return "just now";
        if (diffMin < 60) return `${diffMin} min ago`;
        const diffH = Math.round(diffMin / 60);
        if (diffH < 24) return `${diffH} hour${diffH === 1 ? "" : "s"} ago`;
        return `on ${d.toLocaleString()}`;
    } catch {
        return "";
    }
}
